import React, { createContext, useContext, useEffect, useState } from 'react';
import { api, User, resolveApiPath } from '@/lib/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isCuiHandler: boolean;
  isAuditor: boolean;
  isExternal: boolean;
  /** Server-derived; mirrors the guards on CUI upload and share creation. */
  canSendCui: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  setDevUser: (email: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const loadUser = () => api.get<{ success: boolean; data: User }>(resolveApiPath('/api/auth/me'));

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUser = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await loadUser();
      setUser(response.data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch user'));
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const setDevUser = (email: string) => {
    localStorage.setItem('dev_user_email', email);
    fetchUser();
  };

  useEffect(() => {
    let cancelled = false;
    loadUser().then(response => {
      if (!cancelled) setUser(response.data);
    }).catch(err => {
      if (!cancelled) setError(err instanceof Error ? err : new Error('Failed to fetch user'));
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const roles = user?.roles || [];
  const isAdmin = roles.includes('admin');
  const isCuiHandler = roles.includes('cui-handler') || isAdmin;
  const isAuditor = roles.includes('auditor') || isAdmin;
  const isExternal = user?.isExternal ?? false;
  const canSendCui = user?.canSendCui === true;

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isAdmin,
        isCuiHandler,
        isAuditor,
        isExternal,
        canSendCui,
        error,
        refetch: fetchUser,
        setDevUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
